import * as Y from 'yjs';
import { store } from './db.ts';
import { unwrapDataKey } from '../crypto/session.ts';
import { open as openEnvelope, seal } from '../crypto/vault.ts';
import type { LocalNote } from './types.ts';

/**
 * Registry der offenen Yjs-Dokumente (docs/KONZEPT.md, Abschnitt 5).
 *
 * Jede Notiz ist ein CRDT: `content` als Y.Text, `meta` als Y.Map für Pin-Status,
 * Ordner und Anlagezeitpunkt. Änderungen erzeugen Deltas, die verschlüsselt in die
 * Warteschlange gehen; eingehende Deltas werden mit der Herkunft `remote` angewandt,
 * damit sie nicht als eigene Änderung wieder hinausgehen.
 */

export type NoteDoc = {
  id: string;
  doc: Y.Doc;
  text: Y.Text;
  meta: Y.Map<unknown>;
};

export const REMOTE = 'remote';

type Entry = { note: NoteDoc; refs: number; timer: ReturnType<typeof setTimeout> | null };

const entries = new Map<string, Entry>();
const changeListeners = new Set<(noteId: string) => void>();
let outgoing: ((noteId: string, update: Uint8Array) => void) | null = null;

export function onDocsChanged(listener: (noteId: string) => void): () => void {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

/** Wird von der Abgleichsschicht gesetzt; hier kennen wir den Transportweg nicht. */
export function setOutgoingHandler(handler: (noteId: string, update: Uint8Array) => void): void {
  outgoing = handler;
}

function notify(noteId: string): void {
  for (const listener of changeListeners) listener(noteId);
}

const persistTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Der gespiegelte Klartext und der CRDT-Zustand werden gebündelt geschrieben — bei
 * jedem Tastendruck einzeln in IndexedDB zu schreiben wäre Verschwendung.
 */
function schedulePersist(id: string, local: boolean): void {
  const existing = persistTimers.get(id);
  if (existing) clearTimeout(existing);
  persistTimers.set(
    id,
    setTimeout(() => {
      persistTimers.delete(id);
      void persist(id, local);
    }, 250),
  );
}

async function persist(id: string, local: boolean): Promise<void> {
  const entry = entries.get(id);
  if (!entry) {
    console.warn('[docs] Schreiben verworfen, Dokument war schon geschlossen:', id);
    return;
  }
  const meta = entry.note.meta;
  const content = entry.note.text.toString();
  const state = Y.encodeStateAsUpdate(entry.note.doc);
  const changed = await store.patchNote(id, (note) => ({
    ...note,
    content,
    pinned: meta.get('pinned') === true,
    folderId: (meta.get('folderId') as string | null) ?? null,
    // `?? null`: Ältere Zeilen haben das Feld nicht, und `undefined` würde später
    // als „ist ein Dokument" gelesen werden.
    dokument: (meta.get('dokument') as LocalNote['dokument']) ?? null,
    fund: (meta.get('fund') as LocalNote['fund']) ?? null,
    createdAt: (meta.get('createdAt') as number | undefined) ?? note.createdAt,
    updatedAt: local || note.content !== content ? Date.now() : note.updatedAt,
    state,
  }));
  if (changed) notify(id);
}

async function build(id: string): Promise<NoteDoc> {
  const note = await store.getNote(id);
  const doc = new Y.Doc();
  if (note?.state) Y.applyUpdate(doc, note.state, 'load');
  const entry: NoteDoc = { id, doc, text: doc.getText('content'), meta: doc.getMap('meta') };

  doc.on('update', (update: Uint8Array, origin: unknown) => {
    const local = origin !== REMOTE && origin !== 'load';
    if (local) outgoing?.(id, update);
    schedulePersist(id, local);
  });
  return entry;
}

/**
 * Läufe, die gerade ein Dokument aufbauen. Ohne sie ist `acquire` nicht unteilbar:
 * Zwischen der Feststellung „ist noch nicht offen" und dem Eintragen liegt ein
 * `await`, und ein zweiter Aufrufer in dieser Lücke baut ein zweites Dokument. Beide
 * arbeiten dann an einer eigenen Fassung, aber nur die zuletzt eingetragene wird
 * geschrieben — die Änderungen der anderen verschwinden lautlos.
 *
 * Aufgefallen beim Stapellauf im Archiv: Der Abgleich im Hintergrund öffnete
 * dieselben Dokumente wie die Texterkennung, und zwei von sechs blieben ohne Text.
 */
const building = new Map<string, Promise<NoteDoc>>();

export async function acquire(id: string): Promise<NoteDoc> {
  const existing = entries.get(id);
  if (existing) {
    existing.refs += 1;
    if (existing.timer) {
      clearTimeout(existing.timer);
      existing.timer = null;
    }
    return existing.note;
  }

  let pending = building.get(id);
  if (!pending) {
    pending = build(id).then((note) => {
      entries.set(id, { note, refs: 0, timer: null });
      building.delete(id);
      return note;
    });
    building.set(id, pending);
  }
  const note = await pending;

  // Der Zähler wird erst hier erhöht — jeder Wartende zählt für sich.
  const entry = entries.get(id);
  if (!entry) return note;
  entry.refs += 1;
  if (entry.timer) {
    clearTimeout(entry.timer);
    entry.timer = null;
  }
  return entry.note;
}

export function release(id: string): void {
  const entry = entries.get(id);
  if (!entry) return;
  entry.refs -= 1;
  if (entry.refs > 0) return;
  // Kurz offen halten: Notizwechsel hin und zurück soll nicht jedes Mal neu aufbauen.
  entry.timer = setTimeout(() => {
    const current = entries.get(id);
    if (!current || current.refs > 0) return;
    void persist(id, false).finally(() => {
      // Zwischen Schreiben und Verwerfen liegt ein Wartepunkt, und darin kann das
      // Dokument längst wieder geöffnet worden sein — `acquire` findet den Eintrag
      // ja noch. Wer hier stur zerstört, nimmt dem neuen Benutzer das Dokument unter
      // den Händen weg: Schreibzugriffe verpuffen danach ohne Fehler.
      const now = entries.get(id);
      if (!now || now.refs > 0 || now.note !== current.note) return;
      now.note.doc.destroy();
      entries.delete(id);
    });
  }, 5_000);
}

export async function withDoc<T>(id: string, action: (note: NoteDoc) => T | Promise<T>): Promise<T> {
  const note = await acquire(id);
  try {
    return await action(note);
  } finally {
    release(id);
  }
}

/** Sofortiges Schreiben, ohne auf die Sammelverzögerung zu warten. */
export async function flushPersist(id: string): Promise<void> {
  const timer = persistTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    persistTimers.delete(id);
  }
  await persist(id, false);
}

// --- Verschlüsselung der Deltas -------------------------------------------

export function sealUpdate(update: Uint8Array, wrappedDek: string): string {
  return seal(update, unwrapDataKey(wrappedDek));
}

export function openUpdate(ciphertext: string, wrappedDek: string): Uint8Array {
  return openEnvelope(ciphertext, unwrapDataKey(wrappedDek));
}

/** Wendet ein fremdes Delta an — auch auf Notizen, die gerade nicht geöffnet sind. */
export async function applyRemote(note: LocalNote, ciphertext: string): Promise<void> {
  const update = openUpdate(ciphertext, note.wrappedDek);
  await withDoc(note.id, (entry) => {
    Y.applyUpdate(entry.doc, update, REMOTE);
  });
}

export function encodeState(note: NoteDoc): Uint8Array {
  return Y.encodeStateAsUpdate(note.doc);
}
